import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { FixedLayout } from "@/components/shared/fixed-layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Card, CardContent } from "@/components/ui/card";
import { phoneNumberRegex } from "@/lib/regex";
import { usePostSendSms, usePostVerifySms } from "@/lib/api/sms/hooks";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { apiClient } from "@/lib/api/client";
import { AxiosError } from "axios";

const FIND_ID_TITLE = "아이디 찾기";

const PLACEHOLDER = {
  NAME: "이름을 입력해주세요",
  PHONE_NUMBER: "01012345678",
  VERIFICATION_CODE: "인증번호 6자리를 입력해 주세요.",
};

const ERROR_MESSAGE = {
  NAME_REQUIRED: "이름을 입력해주세요.",
  PHONE_NUMBER_REQUIRED: "휴대전화 번호를 입력해주세요.",
  PHONE_NUMBER_FORMAT:
    "휴대전화 번호는 010으로 시작하는 11자리 숫자여야 합니다.",
  VERIFICATION_CODE_REQUIRED: "인증번호를 입력해주세요.",
  VERIFICATION_CODE_FORMAT: "인증번호는 6자리 숫자여야 합니다.",
};

const VERIFICATION_TIME_LIMIT = 300; // 5분

const findIdSchema = z.object({
  name: z.string().min(1, ERROR_MESSAGE.NAME_REQUIRED),
  phoneNumber: z
    .string()
    .min(1, ERROR_MESSAGE.PHONE_NUMBER_REQUIRED)
    .regex(phoneNumberRegex, ERROR_MESSAGE.PHONE_NUMBER_FORMAT),
  verificationCode: z
    .string()
    .min(1, ERROR_MESSAGE.VERIFICATION_CODE_REQUIRED)
    .regex(/^[0-9]{6}$/, ERROR_MESSAGE.VERIFICATION_CODE_FORMAT)
    .optional(),
});

type FindIdFormData = z.infer<typeof findIdSchema>;

function FindIdPage() {
  const navigate = useNavigate();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [remainingTime, setRemainingTime] = useState(VERIFICATION_TIME_LIMIT);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [foundUserId, setFoundUserId] = useState<string>("");
  const [showResult, setShowResult] = useState(false);

  const { mutateAsync: sendSms, isPending: isSending } = usePostSendSms();
  const { mutateAsync: verifySms, isPending: isVerifying } = usePostVerifySms();

  useLoadingIndicator(isSending || isVerifying);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    trigger,
    getValues,
  } = useForm<FindIdFormData>({
    resolver: zodResolver(findIdSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      phoneNumber: "",
      verificationCode: "",
    },
  });

  const handleSendVerification = async () => {
    const phone = getValues("phoneNumber");
    const name = getValues("name");

    if (!name || !phone) return;

    try {
      await sendSms({ phoneNumber: phone, name });
      setIsVerificationSent(true);
      setRemainingTime(VERIFICATION_TIME_LIMIT);
      setCooldownTime(60);

      setAlertDialogState({
        title: "인증번호 전송",
        description: "입력하신 휴대전화 번호로 인증번호를 전송했습니다.",
        onConfirm: () => {
          resetAlertDialogState();
        },
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        setAlertDialogState({
          title: "인증번호 전송 실패",
          description:
            error.response?.data?.message ||
            "인증번호 전송에 실패했습니다. 이름과 휴대전화 번호를 확인해주세요.",
          onConfirm: () => {
            resetAlertDialogState();
          },
        });
      }
    }
  };

  const onSubmit = async (data: FindIdFormData) => {
    try {
      console.log("아이디 찾기 시작:", data);

      // SMS 인증 확인
      await verifySms({
        phoneNumber: data.phoneNumber,
        code: data.verificationCode!,
        name: data.name,
      });
      console.log("SMS 인증 성공");

      // 아이디 찾기 API 호출
      const response = await apiClient.post<{ userId: string }>(
        "/auth/find-id",
        {
          name: data.name,
          phoneNumber: data.phoneNumber,
        }
      );
      console.log("아이디 찾기 성공:", response.data);

      // 아이디 일부 마스킹 (user1234 → use****)
      const userId = response.data.userId;
      const maskedUserId =
        userId.slice(0, 3) + "*".repeat(Math.max(userId.length - 3, 1));

      setFoundUserId(maskedUserId);
      setShowResult(true);
    } catch (error: unknown) {
      console.error("아이디 찾기 실패:", error);
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "일치하는 회원 정보를 찾을 수 없습니다.";

        console.log("에러 메시지:", errorMessage);

        setAlertDialogState({
          title: "아이디 찾기 실패",
          description: errorMessage,
          onConfirm: () => {
            resetAlertDialogState();
          },
        });
      } else {
        setAlertDialogState({
          title: "아이디 찾기 실패",
          description: "일치하는 회원 정보를 찾을 수 없습니다.",
          onConfirm: () => {
            resetAlertDialogState();
          },
        });
      }
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handlePhoneNumberChange = (
    value: string,
    onChange: (...event: unknown[]) => void
  ) => {
    const digitsOnly = value.replace(/[^0-9]/g, "");
    onChange(digitsOnly);
  };

  // 인증번호 타이머
  useEffect(() => {
    if (isVerificationSent && remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isVerificationSent, remainingTime]);

  // 쿨다운 타이머
  useEffect(() => {
    if (cooldownTime > 0) {
      const timer = setInterval(() => {
        setCooldownTime((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldownTime]);

  // 아이디 찾기 결과 화면
  if (showResult) {
    return (
      <FixedLayout
        headerType="back"
        title="아이디 찾기 완료"
        showButton={false}
      >
        <div className="flex flex-col gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="text-sm text-muted-foreground">
                  회원님의 아이디는
                </div>
                <div className="text-2xl font-bold text-primary">
                  {foundUserId}
                </div>
                <div className="text-sm text-muted-foreground">입니다.</div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2">
            <Button onClick={() => navigate({ to: "/auth/login" })} size="lg">
              로그인하러 가기
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/auth/find-password" })}
              size="lg"
            >
              비밀번호 재설정
            </Button>
          </div>
        </div>
      </FixedLayout>
    );
  }

  return (
    <FixedLayout
      headerType="back"
      title={FIND_ID_TITLE}
      showButton={isVerificationSent}
      buttonText="아이디 찾기"
      buttonDisabled={!isValid || remainingTime === 0 || isVerifying}
      onSubmit={handleSubmit(onSubmit)}
    >
      <form id="find-id-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          {/* 이름 입력 */}
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="find-id-name">이름</FieldLabel>
                <Input
                  {...field}
                  id="find-id-name"
                  type="text"
                  placeholder={PLACEHOLDER.NAME}
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* 휴대전화 번호 입력 */}
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="find-id-phone-number">
                  휴대전화 번호
                </FieldLabel>
                <div className="w-full flex gap-2">
                  <Input
                    {...field}
                    id="find-id-phone-number"
                    type="tel"
                    placeholder={PLACEHOLDER.PHONE_NUMBER}
                    aria-invalid={fieldState.invalid}
                    onChange={(e) =>
                      handlePhoneNumberChange(e.target.value, field.onChange)
                    }
                    wrapperClassName="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={async () => {
                      const isNameValid = await trigger("name");
                      const isPhoneValid = await trigger("phoneNumber");
                      if (isNameValid && isPhoneValid) {
                        await handleSendVerification();
                      }
                    }}
                    disabled={
                      !field.value ||
                      fieldState.invalid ||
                      isSending ||
                      cooldownTime > 0
                    }
                  >
                    {cooldownTime > 0
                      ? `${cooldownTime}초 후 재발송`
                      : isVerificationSent
                        ? "인증번호 재발송"
                        : "인증번호 받기"}
                  </Button>
                </div>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* 인증번호 입력 */}
          {isVerificationSent && (
            <Controller
              name="verificationCode"
              control={control}
              render={({ field, fieldState }) => (
                <Field>
                  <div className="flex justify-between items-center">
                    <FieldLabel htmlFor="find-id-verification-code">
                      인증번호
                    </FieldLabel>
                    <span
                      className={`text-sm ${
                        remainingTime > 0 ? "text-gray-600" : "text-red-500"
                      }`}
                    >
                      {remainingTime > 0
                        ? formatTime(remainingTime)
                        : "시간 만료"}
                    </span>
                  </div>
                  <Input
                    {...field}
                    id="find-id-verification-code"
                    type="text"
                    inputMode="numeric"
                    placeholder={PLACEHOLDER.VERIFICATION_CODE}
                    aria-invalid={fieldState.invalid}
                    maxLength={6}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      field.onChange(value);
                    }}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          )}
        </div>
      </form>
    </FixedLayout>
  );
}

export const Route = createFileRoute("/auth/find-id")({
  component: FindIdPage,
});
